/**
 * Page principale de gestion des Worksheets - Manager View
 */

import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Worksheet,
  Workstation,
  WorksheetStatus,
  WorksheetType,
} from '../../types/worksheets.types';
import {
  getWorksheets,
  getWorkstations,
  cancelWorksheet,
  seedWorkstations,
} from '../../services/worksheets.api';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import WorksheetValidationModal from '../../components/worksheets/WorksheetValidationModal';
import WorksheetAssignModal from '../../components/worksheets/WorksheetAssignModal';
import WorksheetCreateModal from '../../components/worksheets/WorksheetCreateModal';
import WorksheetDetailModal from '../../components/worksheets/WorksheetDetailModal';

// Status badge colors
const STATUS_COLORS: Record<WorksheetStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  TODO: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<WorksheetStatus, string> = {
  DRAFT: 'Brouillon',
  TODO: 'A faire',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

const TYPE_LABELS: Record<WorksheetType, string> = {
  OPENING: 'Ouverture',
  CLOSING: 'Fermeture',
  RECEPTION: 'Réception',
  PREPARATION: 'Préparation',
  INVENTORY: 'Inventaire',
  MAINTENANCE: 'Maintenance',
  CUSTOM: 'Personnalisé',
};

interface WorksheetStats {
  total: number;
  by_status: Record<WorksheetStatus, number>;
  today_completed: number;
  today_pending: number;
}

export default function WorksheetsDashboard() {
  // State
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [stats, setStats] = useState<WorksheetStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<WorksheetStatus | ''>('');
  const [workstationFilter, setWorkstationFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<WorksheetType | ''>('');

  // Modals
  const [selectedWorksheet, setSelectedWorksheet] = useState<Worksheet | null>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [worksheetsData, workstationsData] = await Promise.all([
        getWorksheets({
          status: statusFilter || undefined,
          workstation_id: workstationFilter || undefined,
          type: typeFilter || undefined,
        }),
        getWorkstations(),
      ]);

      setWorksheets(worksheetsData);
      setWorkstations(workstationsData);

      // Calcul des stats localement
      const statsByStatus = worksheetsData.reduce((acc, ws) => {
        acc[ws.status] = (acc[ws.status] || 0) + 1;
        return acc;
      }, {} as Record<WorksheetStatus, number>);

      const today = new Date().toISOString().split('T')[0];
      const todayWorksheets = worksheetsData.filter(
        (ws) => ws.created_at.startsWith(today)
      );

      setStats({
        total: worksheetsData.length,
        by_status: statsByStatus,
        today_completed: todayWorksheets.filter((ws) => ws.status === 'COMPLETED').length,
        today_pending: todayWorksheets.filter(
          (ws) => ws.status === 'TODO' || ws.status === 'IN_PROGRESS'
        ).length,
      });
    } catch (err: any) {
      console.error('Failed to load worksheets:', err);
      if (err?.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else {
        setError('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, workstationFilter, typeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Seed workstations si vide
  const handleSeedWorkstations = async () => {
    try {
      await seedWorkstations();
      await loadData();
    } catch (err) {
      console.error('Failed to seed workstations:', err);
    }
  };

  // Actions sur worksheet
  const handleValidate = (worksheet: Worksheet) => {
    setSelectedWorksheet(worksheet);
    setShowValidationModal(true);
  };

  const handleAssign = (worksheet: Worksheet) => {
    setSelectedWorksheet(worksheet);
    setShowAssignModal(true);
  };

  const handleViewDetail = (worksheet: Worksheet) => {
    setSelectedWorksheet(worksheet);
    setShowDetailModal(true);
  };

  const handleCancel = async (worksheet: Worksheet) => {
    if (!confirm('Voulez-vous vraiment annuler cette worksheet ?')) return;
    try {
      await cancelWorksheet(worksheet.id);
      await loadData();
    } catch (err) {
      console.error('Failed to cancel worksheet:', err);
    }
  };

  // Close modals and refresh
  const handleModalClose = () => {
    setShowValidationModal(false);
    setShowAssignModal(false);
    setShowCreateModal(false);
    setShowDetailModal(false);
    setSelectedWorksheet(null);
    loadData();
  };

  // Get workstation name
  const getWorkstationName = (id: string) => {
    const ws = workstations.find((w) => w.id === id);
    return ws?.name || id;
  };

  if (loading && worksheets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Worksheets</h1>
          <p className="text-gray-500 mt-1">
            Gestion des tâches et fiches de travail
          </p>
        </div>
        <div className="flex gap-3">
          {workstations.length === 0 && (
            <Button variant="outline" onClick={handleSeedWorkstations}>
              Initialiser les postes
            </Button>
          )}
          <Button onClick={() => setShowCreateModal(true)}>
            + Nouvelle Worksheet
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm text-gray-500">Total</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm text-gray-500">En attente</div>
            <div className="text-2xl font-bold text-blue-600">
              {(stats.by_status?.TODO || 0) + (stats.by_status?.DRAFT || 0)}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm text-gray-500">En cours</div>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.by_status?.IN_PROGRESS || 0}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm text-gray-500">Terminées (aujourd'hui)</div>
            <div className="text-2xl font-bold text-green-600">
              {stats.today_completed}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as WorksheetStatus | '')}
          className="w-40"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>

        <Select
          value={workstationFilter}
          onChange={(e) => setWorkstationFilter(e.target.value)}
          className="w-48"
        >
          <option value="">Tous les postes</option>
          {workstations.map((ws) => (
            <option key={ws.id} value={ws.id}>
              {ws.name}
            </option>
          ))}
        </Select>

        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as WorksheetType | '')}
          className="w-40"
        >
          <option value="">Tous les types</option>
          {Object.entries(TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>

        <Button variant="outline" size="sm" onClick={loadData}>
          Actualiser
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Worksheets List */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Worksheet
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Poste
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigné à
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Créée le
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {worksheets.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  Aucune worksheet trouvée
                </td>
              </tr>
            ) : (
              worksheets.map((worksheet) => (
                <tr
                  key={worksheet.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewDetail(worksheet)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">
                      {worksheet.title}
                    </div>
                    {worksheet.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {worksheet.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {getWorkstationName(worksheet.workstation_id)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {TYPE_LABELS[worksheet.type]}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        STATUS_COLORS[worksheet.status]
                      }`}
                    >
                      {STATUS_LABELS[worksheet.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {worksheet.assigned_to_name || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {format(new Date(worksheet.created_at), 'dd/MM/yyyy HH:mm', {
                      locale: fr,
                    })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div
                      className="flex gap-2 justify-end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {worksheet.status === 'DRAFT' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleValidate(worksheet)}
                          >
                            Valider
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCancel(worksheet)}
                          >
                            Annuler
                          </Button>
                        </>
                      )}
                      {worksheet.status === 'TODO' && !worksheet.assigned_to_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAssign(worksheet)}
                        >
                          Assigner
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showValidationModal && selectedWorksheet && (
        <WorksheetValidationModal
          worksheet={selectedWorksheet}
          onClose={handleModalClose}
        />
      )}

      {showAssignModal && selectedWorksheet && (
        <WorksheetAssignModal
          worksheet={selectedWorksheet}
          onClose={handleModalClose}
        />
      )}

      {showCreateModal && (
        <WorksheetCreateModal
          workstations={workstations}
          onClose={handleModalClose}
        />
      )}

      {showDetailModal && selectedWorksheet && (
        <WorksheetDetailModal
          worksheetId={selectedWorksheet.id}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}
